import { useMemo } from 'react';
import type { TOC } from '~/common/types';
import { DOWNLOAD_LABELS } from '~/hooks/useExportTerminologyEnabled';
import GlobalCommands from '../GlobalCommands';
import { type LayerPickerProps, usePickerRows } from './pickerRows';

export const LayerPickerLegacy = ({ currentObjectArea, extraSections, labels = DOWNLOAD_LABELS }: LayerPickerProps) => {
  const {
    renderTree,
    selectedLayerIds,
    TOCLayers,
    expandedSections,
    restrictions,
    layerIdsWithUnknownAssets,
    isCheckingLayerAssets,
  } = usePickerRows({
    currentObjectArea,
    extraSections,
    labels,
    searchTerm: '',
  });

  // The whole selection snapshot is a dep, not just its size: section rows render a none/some/all
  // state, and a swap (one leaf off, another on) leaves the size unchanged.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const withinItems = useMemo(
    () => renderTree(TOCLayers as TOC, { group: 'within', showCheckbox: true }),
    [
      selectedLayerIds,
      TOCLayers,
      expandedSections,
      layerIdsWithUnknownAssets.size,
      labels,
      restrictions,
      currentObjectArea,
    ],
  );

  const extraItems = renderTree(extraSections ?? [], { group: 'extra', showCheckbox: true });

  return (
    <div className="pointer-events-auto flex min-h-0 flex-col rounded-sm bg-white">
      <div className="flex w-full shrink-0 items-center justify-between p-2">
        <div className="flex flex-row space-x-2">
          <div className="text-base font-semibold">Layers</div>
        </div>
        <div className="flex items-center space-x-2">
          <GlobalCommands downloadArea={currentObjectArea} />
        </div>
      </div>

      <div className="shrink-0 px-2 pb-2 text-2xs text-grey-60" data-testid="download-layer-picker-assets-only-note">
        {isCheckingLayerAssets ? (
          'Checking which layers have data in your project area…'
        ) : (
          <>
            Only layers with assets inside your project boundary are listed.
            <br />
            Layers we could not verify are marked with a warning.
          </>
        )}
      </div>

      <div className="my-1 min-h-0 max-h-96 flex-1 overflow-y-auto pr-2">
        {/* First → top, matching store2/layers.ts setCustomDataTOCGroup (see extraSections prop). */}
        {extraItems}
        {withinItems}
      </div>
    </div>
  );
};
